import React, {useContext, useEffect, useState} from 'react';
import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import AdmissionDischargeCRFTable from "@/components/pages/analytics/AdmissionDischargeCRFTable";
import {FiltersRow} from "@/components/pages/analytics/FilterDropDown";
import {
  CountBottomLabel,
  CountBox,
  CountBoxContainer,
  CountNumber,
  CountTopLabel,
  ExportButtonWrapper,
  FiltersAndTabsContainer,
  FiltersWrapper,
  HeaderWithExportContainer,
  StyledDisplayLargeTypography,
  TableContainer
} from '@/components/pages/analytics/IncompleteCRFReport.styles';
import Spacer from "patient-ping-remedy/packages/spacer";
import DisplayHelpers from "@/helpers/display_helpers";
import {css} from "@emotion/css";
import {colors, fontSize} from "patient-ping-remedy/packages/theme";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Icon from "patient-ping-remedy/packages/icon";
import {nonGroupedValues} from "@common/FilterComponent";
import {TableColumn} from "@common/SortableTable";
import {SortOrderType} from "@clients/ClientsPage";
import ExportModal from "@/components/pages/analytics/ExportModal";
import FileHelpers from "@/helpers/file_helpers";
import {log} from "@/helpers/logger";

export default function AdmissionDischargeCRFsReport() {
  const { carecoApi } = useContext(CarecoApiContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState<{
    rosterIds: string[],
    assigneeIds: string[],
    formTypes: string[],
    crfCompleted: string[],
    sortField?: string,
    sortDirection?: string,
  }>({
    rosterIds: [],
    assigneeIds: [],
    formTypes: [],
    crfCompleted: [],
  });

  const { data: rosters } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi
  });

  const { data: assigneeOptions, isLoading: assigneeLoading } = useQuery({
    queryKey: ['assignedCrfReportOptions'],
    queryFn: () => carecoApi?.getUsersAssignedToClients().then(users => [
      {
        id: 'Unassigned',
        value: 'Unassigned',
        label: 'Unassigned',
      },
      ...users.map((user): nonGroupedValues => ({
        id: user.id,
        value: user.id,
        label: `${user.first_name} ${user.last_name}`,
      })),
    ]),
    enabled: !!carecoApi,
  });

  const { data: dischargeClientsData, isLoading: dischargeLoading, error: dischargeError,
    isFetching: dischargeFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ['incompleteDischargeCRFs', filters],
    refetchOnWindowFocus: false,
    staleTime: 0,
    queryFn: ({ pageParam }) => carecoApi?.getIncompleteDischargeCRFs(
      filters.rosterIds,
      filters.assigneeIds,
      filters.formTypes,
      filters.sortField,
      filters.sortDirection,
      pageParam,
      25,
    ),
    enabled: !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const dischargeClients = dischargeClientsData?.pages
    .flatMap((page) => page?.data.incompleteAdmissionAndDischargeCrfs ?? []) ?? [];

  const [dischargeCRFCount, setDischargeCRFCount] = useState(0);
  const [crfsReaching90Days, setCrfsReaching90Days] = useState(0);

  useEffect(() => {
    if (dischargeClientsData) {
      setDischargeCRFCount(dischargeClientsData?.pages[0]?.data.totalIncompleteAdmissionAndDischargeCount ?? 0);
      setCrfsReaching90Days(dischargeClientsData?.pages[0]?.data.approaching90DaysSinceDischargeCount ?? 0);
    }
  }, [dischargeClientsData]);

  const searchFunction = (term: string) => {
    setSearchTerm(term);
  };

  const sortFunction = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    setFilters((filters) => ({
      ...filters,
      sortField: sortKey.key,
      sortDirection: sortOrder.value,
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    const headers: string[] = [
      'Client name',
      'Client Id',
      'Group',
      'Client discharge date',
      'No of Days since discharge',
      'Assigned to',
      'Incomplete form',
    ];

    try {
      const data = await carecoApi?.getIncompleteDischargeCRFs(
        filters.rosterIds, filters.assigneeIds, filters.formTypes, filters.sortField, filters.sortDirection);
      const dataToExport = data?.data.incompleteAdmissionAndDischargeCrfs ?? [];

      const rows = dataToExport.map(client => {
        const [firstName, middleName, lastName] = client.clientName.split(' ');
        const clientName = DisplayHelpers.formatName(firstName, middleName, lastName);
        const daysSinceDischarge = DisplayHelpers.daysSince(client.clientDischargeDate);

        const assignedUsers = client.client.userAssignments
          ?.map(assignment => `${assignment.user.first_name} ${assignment.user.last_name}`)
          .join(', ') || 'Unassigned';

        return [
          clientName,
          client.clientId,
          client.clientGroup,
          client.clientDischargeDate,
          daysSinceDischarge,
          assignedUsers,
          client.clientDischargeForm
            ? 'Discharge CRF'
            : client.clientAdmissionForm
              ? 'Admission CRF'
              : '',
        ];
      });

      const csvContent: string = [
        headers,
        ...rows
      ].map(row => row.map(cell => cell ? `${cell}` : "").join(","))
        .join("\n");


      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-16;' });
      const fileName = 'incomplete-admission-and-discharge-crfs.csv';

      FileHelpers.downloadFile(URL.createObjectURL(blob), fileName);
      setShowExportModal(false);
    } catch (error) {
      log('error', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <StyledDisplayLargeTypography>Admission + Discharge CRF  <Spacer itemWidth={"2rem"} />
      </StyledDisplayLargeTypography>
      <span className={css({ fontWeight: 400, color: colors.gray5, fontSize: fontSize.displayLarge })}>
        {DisplayHelpers.formatDate(new Date())}</span>
      <div>
        <Spacer itemHeight={"1rem"} />
      </div>
      <HeaderWithExportContainer>
        <FiltersAndTabsContainer>
          <FiltersWrapper>
            <FiltersRow
              rosters={rosters}
              showIncompleteForm={true}
              showCRFCompleted={false}
              assigneeOptions={assigneeOptions}
              assigneeLoading={assigneeLoading}
              onFiltersChange={setFilters}
            />
          </FiltersWrapper>
        </FiltersAndTabsContainer>
        <ExportButtonWrapper>
          <Button
            styleType={StyleType.SECONDARY}
            disabled={isExporting}
            onClick={() => setShowExportModal(true)}
          >
            <Icon iconClass={'download'} style={{ marginRight: '10px' }} />
            Export
          </Button>
        </ExportButtonWrapper>
      </HeaderWithExportContainer>
      <FiltersWrapper>
        <CountBoxContainer>
          <>
            <CountBox>
              <CountTopLabel>Incomplete CRF</CountTopLabel>

              <CountNumber>{dischargeCRFCount}</CountNumber>

              <CountBottomLabel>
                Admission + Discharge CRF in total
              </CountBottomLabel>
            </CountBox>

            <CountBox>
              <CountTopLabel>CRF approaching 90 days</CountTopLabel>

              <CountNumber>{crfsReaching90Days}</CountNumber>

              <CountBottomLabel>
                Discharge CRF in total
              </CountBottomLabel>
            </CountBox>
          </>
        </CountBoxContainer>
      </FiltersWrapper>

      <TableContainer>
        <AdmissionDischargeCRFTable
          clients={dischargeClients}
          loading={dischargeLoading}
          fetching={dischargeFetching}
          loadError={dischargeError}
          searchTerm={searchTerm}
          searchFunction={searchFunction}
          sortFunction={sortFunction}
          onNextPage={fetchNextPage}
        />
      </TableContainer>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={() => handleExport()}
        isExporting={isExporting}
      ></ExportModal>
    </div>
  );
}


